import api from '../api/axios';
import {
  Evaluador,
  EvaluadorCarga,
  EvaluadorCrearInput,
  EvaluadorInput,
  EvaluadorResumen,
  EvaluadorScope,
  Periodo,
  ResultadoNotificacion,
  ResultadoSyncEvaluadores,
} from '../types/evaluador';

export const evaluadorService = {
  /** Correo del coordinador a los docentes indicados (los emails se resuelven en el servidor). */
  notificar: async (payload: {
    ruts: number[];
    asunto: string;
    cuerpo: string;
    incluir_pendientes: boolean;
  }): Promise<ResultadoNotificacion> => {
    const { data } = await api.post<ResultadoNotificacion>('coordinacion/evaluadores/notificar/', payload);
    return data;
  },

  /** Ficha de un docente (coordinador): temas, guía/coguía y su actividad como evaluador. */
  resumen: async (rut: number): Promise<EvaluadorResumen> => {
    const { data } = await api.get<EvaluadorResumen>(`evaluadores/${rut}/resumen/`);
    return data;
  },

  /** Catálogo de evaluadores (coordinador); búsqueda por nombre/rut y carga acotada a un período/cohorte. */
  list: async (q: string, scope?: EvaluadorScope, ocultos = false): Promise<Evaluador[]> => {
    const params: Record<string, string | number> = {};
    if (q) params.q = q;
    if (scope?.ano) params.ano = scope.ano;
    if (scope?.semestre !== undefined) params.semestre = scope.semestre;
    if (scope?.paso) params.paso = scope.paso;
    if (ocultos) params.ocultos = 1;
    const { data } = await api.get<Evaluador[]>('evaluadores/', { params });
    return data;
  },

  /** Carga pendiente por docente (estudiantes con su último paso abierto), para el gráfico. */
  carga: async (scope?: EvaluadorScope): Promise<EvaluadorCarga[]> => {
    const params: Record<string, number> = {};
    if (scope?.ano && scope.semestre !== undefined) {
      params.ano = scope.ano;
      params.semestre = scope.semestre;
    }
    const { data } = await api.get<EvaluadorCarga[]>('evaluadores/carga/', { params });
    return data;
  },

  /** Oculta a un docente. No borra nada: apaga `vigente` (se devuelve con `update`). */
  ocultar: async (rut: number): Promise<Evaluador> => {
    const { data } = await api.delete<Evaluador>(`evaluadores/${rut}/`);
    return data;
  },

  /** Períodos (año + semestre) presentes en los procesos. */
  periodos: async (): Promise<Periodo[]> => {
    const { data } = await api.get<Periodo[]>('coordinacion/periodos/');
    return data;
  },

  /**
   * Da de alta un docente por su RUT. Nace en el directorio (Factos, como profesor
   * externo) y recién ahí en titulación; `aviso` trae lo que haya que decirle a
   * quien lo agregó (ya existía allá, ya era académico, directorio no configurado).
   */
  create: async (input: EvaluadorCrearInput): Promise<Evaluador & { aviso?: string | null }> => {
    const { data } = await api.post<Evaluador & { aviso?: string | null }>('evaluadores/', input);
    return data;
  },

  /** Edita flags de comisión/evaluación y/o las áreas de un evaluador. */
  update: async (rut: number, input: EvaluadorInput): Promise<Evaluador> => {
    const { data } = await api.patch<Evaluador>(`evaluadores/${rut}/`, input);
    return data;
  },

  /** Sincroniza los evaluadores desde api-personas. commit=false → preview (dry-run). */
  sincronizar: async (commit: boolean): Promise<ResultadoSyncEvaluadores> => {
    const { data } = await api.post<ResultadoSyncEvaluadores>('coordinacion/evaluadores/sincronizar/', { commit });
    return data;
  },
};
